import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Modal, Typography, generateUtilityClasses } from '@mui/material'
import { styled } from '@mui/system'
import React, { useEffect, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import DropdownFieldSearch from '../../../../ui/DropdownFieldSearch'
import { CustomCircular, TextField } from '../../../../ui'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import { useAddPaymentMethodMutation, useEditPaymentMethodMutation, useLazyPaymentMethodDataQuery } from '../../../../services'
import { UspConfigurationPaymentMethodViewResult } from '../../../../models/apiTypes'

const classes = generateUtilityClasses('Box', ['root', 'row', 'buttonWrap', 'saveBtn'])

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '390px',
    backgroundColor: theme.palette.background.main,
    boxShadow: 24,
    padding: theme.spacing(3),
  },
  [`& .${classes.row}`]: {
    margin: theme.spacing(1, 0, 2, 0),
  },
  [`& .${classes.buttonWrap}`]: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row-reverse',
    padding: theme.spacing(3, 0, 2, 0),
    marginTop: 'auto',
  },
  [`& .${classes.saveBtn}`]: {
    marginLeft: theme.spacing(2),
  },
}))

interface Props {
  rowData: UspConfigurationPaymentMethodViewResult
  open: boolean
  onClose: () => void
  type: string
  submit: (...args: any) => any
}

const AddEditPaymentMethodModal = ({ open, onClose, rowData, type, submit }: Props) => {
  const [initialValues, setInitialValues] = useState({})
  const [addPaymentMethod, { isLoading: isLoadingPaymentMethod, isSuccess: isSuccessPaymentMethod }] = useAddPaymentMethodMutation()
  const [editPaymentMethod, { isLoading: isLoadingEditPaymentMethod, isSuccess: isSuccessEditPaymentMethod }] = useEditPaymentMethodMutation()
  const [getPaymentMethodData, { isLoading: isLoadingGetPaymentMethod, isSuccess: isSuccessGetPaymentMethod, data }] = useLazyPaymentMethodDataQuery()

  const handleSubmit = (values: any) => {
    if (type === 'edit') {
      editPaymentMethod({
        paymentMethodId: rowData?.ID?.toString(),
        uspConfigurationPaymentMethodAddEdit: {
          paymentMethod: values.Description,
          isWaiveProcessingFee: values.WaivesProcessingFee,
          isActive: values.IsActive,
        },
      })
    } else {
      addPaymentMethod({
        uspConfigurationPaymentMethodAddEdit: {
          paymentMethod: values.Description,
          isWaiveProcessingFee: values.WaivesProcessingFee,
          isActive: values.IsActive,
        },
      })
    }
  }

  useEffect(() => {
    if (open && rowData && type === 'edit')
      getPaymentMethodData({
        paymentMethodId: rowData?.ID?.toString() as string,
      })
  }, [open, type, rowData])

  useEffect(() => {
    if (type === 'add') {
      setInitialValues({
        Description: '',
        IsActive: true,
        WaivesProcessingFee: true,
      })
    }
    if (isSuccessGetPaymentMethod && type === 'edit') {
      setInitialValues({
        Description: data?.PaymentMethod,
        IsActive: data?.IsActive,
        WaivesProcessingFee: data?.IsWaiveProcessingFee,
      })
    }
  }, [isSuccessGetPaymentMethod, data, type])

  useEffect(() => {
    if (isSuccessPaymentMethod || isSuccessEditPaymentMethod) {
      onClose()
      submit()
    }
  }, [isSuccessPaymentMethod, isSuccessEditPaymentMethod])

  const isLoading = useMemo(
    () => isLoadingPaymentMethod || isLoadingEditPaymentMethod || isLoadingGetPaymentMethod,
    [isLoadingPaymentMethod, isLoadingEditPaymentMethod, isLoadingGetPaymentMethod],
  )
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Payment Method</DialogTitle>
      <Form onSubmit={handleSubmit} initialValues={initialValues}>
        {({ handleSubmit }: any) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2} py={1}>
                <Grid item xs={12}>
                  <TextField required name='Description' label='Description' fullWidth />
                </Grid>

                <Grid item xs={12}>
                  <DropdownFieldSearch
                    name='WaivesProcessingFee'
                    label='Waives Processing Fee?'
                    options={[
                      { key: true, values: 'Yes' },
                      { key: false, values: 'No' },
                    ]}
                    optionKey='key'
                    optionValue='values'
                  />
                </Grid>

                <Grid item xs={12}>
                  <DropdownFieldSearch
                    name='IsActive'
                    label='Is Active?'
                    options={[
                      { key: true, values: 'Yes' },
                      { key: false, values: 'No' },
                    ]}
                    optionKey='key'
                    optionValue='values'
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <LoadingButton type='submit' className={classes.saveBtn} variant='contained'>
                Save
              </LoadingButton>
              <Button variant='contained' color='error' onClick={onClose}>
                Cancel
              </Button>
            </DialogActions>
          </form>
        )}
      </Form>
      <CustomCircular open={isLoading} />
    </Dialog>
  )
}

export default AddEditPaymentMethodModal
